import I18n from 'I18n';
import { ALL_PRESET_ID, CUSTOM_PRESET_ID, DATE_RANGE_PRESETS } from 'automation-ui-management/constants/DateRangePickerOptions';
import { DateFormats, MomentUnits, Timezones, UEFrequencies, WEEKDAYS_AS_INTS } from 'automation-ui-management/constants/DateConstants';
import { fromMoment, makeMoment, toArray, toFormattedString, toMomentPortalTz, equals as areSimpleDatesEqual } from 'foundations-components/transitional/utils/SimpleDate';
export const toDateStringFromTimestamp = timestamp => {
  return timestamp ? I18n.moment(timestamp).format(DateFormats.DATE_QUERY_PARAM_FORMAT) : null;
};
export function toSimpleDate(date) {
  return date ? fromMoment(I18n.moment(date)) : fromMoment(I18n.moment());
}
export const toDateStringFromSimpleDate = simpleDate => {
  return simpleDate ? I18n.moment(toArray(simpleDate)).format(DateFormats.DATE_QUERY_PARAM_FORMAT) : null;
};

// Dev note: SimpleDate's fromString method does not work on the dateint format (SE)
export const fromDateInt = dateInt => fromMoment(I18n.moment.portalTz(`${dateInt}`, DateFormats.DATEINT_FORMAT));
export const toDateInt = date => Number(toFormattedString(date, DateFormats.DATEINT_FORMAT));

// This method is for unit testing convenience (SE)
export let getFirstDayOfWeek = () => I18n.moment().localeData().firstDayOfWeek();
export const getFrequencyForLocale = frequency => {
  const firstDayOfWeek = getFirstDayOfWeek();
  if (frequency === UEFrequencies.WEEKLY && firstDayOfWeek === WEEKDAYS_AS_INTS.MONDAY) {
    return UEFrequencies.WEEKLY_STARTING_MONDAY;
  }
  return frequency;
};
export function getDateRangeRequestFilters(dateRange) {
  const {
    presetId
  } = dateRange;
  const dateRangeOption = DATE_RANGE_PRESETS[presetId || ''];
  if (dateRangeOption) {
    const {
      startDate,
      endDate
    } = dateRangeOption.getValue(Timezones.PORTAL_TZ);
    return {
      startTimestamp: toMomentPortalTz(startDate).valueOf(),
      endTimestamp: toMomentPortalTz(endDate).add(1, 'days').valueOf()
    };
  }
  const {
    startDate,
    endDate
  } = dateRange;
  const startTimestamp = startDate ? toMomentPortalTz(startDate).valueOf() : undefined;
  const endTimestamp = endDate ? toMomentPortalTz(endDate).add(1, MomentUnits.DAY).valueOf() : undefined;
  return {
    startTimestamp,
    endTimestamp
  };
}
export const makeDateRangeFromParsedSearchString = ({
  rangeType,
  startDate,
  endDate
}) => {
  if (rangeType === CUSTOM_PRESET_ID) {
    const isStartDateValid = startDate && I18n.moment(startDate).isValid();
    const isEndDateValid = endDate && I18n.moment(endDate).isValid();
    return {
      presetId: rangeType,
      startDate: isStartDateValid ? toSimpleDate(startDate) : undefined,
      endDate: isEndDateValid ? toSimpleDate(endDate) : undefined
    };
  } else if (rangeType && rangeType in DATE_RANGE_PRESETS) {
    return Object.assign({
      presetId: rangeType
    }, DATE_RANGE_PRESETS[rangeType].getValue(Timezones.PORTAL_TZ));
  }
  return {
    presetId: ALL_PRESET_ID,
    startDate: undefined,
    endDate: undefined
  };
};
export const getDefaultCustomDateRangeForFlow = (__oldFlow, minDate = null, flow) => {
  const flowCreateMetadata = flow.createMetadata;
  const flowCreatedAt = flowCreateMetadata ? flowCreateMetadata.updatedAt : null;
  const today = toSimpleDate();
  let startDate = flowCreatedAt ? toSimpleDate(flowCreatedAt) : today;
  if (minDate && startDate < minDate) {
    startDate = minDate;
  }
  return {
    startDate,
    endDate: today
  };
};
export const presetOptionResolver = (handleDateChangeCallback, defaultPreset) => {
  return event => {
    const {
      target: {
        value
      }
    } = event;
    if (!value.presetId) {
      // If there is a start or end date, the range type is custom.
      if (value.startDate || value.endDate) {
        value.presetId = CUSTOM_PRESET_ID;
        // If list of options exists, set the first as default value
      } else if (defaultPreset) {
        Object.assign(value, defaultPreset);
      } else {
        value.presetId = ALL_PRESET_ID;
      }
    }
    handleDateChangeCallback(event);
  };
};
export const setDateRangeBoundsForUEVisualization = (dateRange, momentUnit) => {
  if (momentUnit === MomentUnits.DAY) {
    return dateRange;
  }
  const start = makeMoment(Timezones.PORTAL_TZ, dateRange.startDate);
  if (!('presetId' in dateRange)) {
    return {
      startDate: fromMoment(start.startOf(momentUnit)),
      endDate: dateRange.endDate
    };
  }
  return {
    presetId: dateRange.presetId,
    startDate: fromMoment(start.startOf(momentUnit)),
    endDate: dateRange.endDate
  };
};
export const splitDateRange = (range, momentUnit, constructor = Timezones.USER_TZ) => {
  const start = makeMoment(constructor, range.startDate);
  const end = makeMoment(constructor, range.endDate);
  const dates = [];
  function addDate() {
    dates.push(fromMoment(start));
    start.add(1, momentUnit);
  }
  addDate();
  while (start.isBefore(end) || start.isSame(end)) {
    addDate();
  }
  return dates;
};
export const findMatchingDateRangePreset = ({
  startDate,
  endDate
}, presets = Object.values(DATE_RANGE_PRESETS)) => presets.find(preset => {
  const {
    startDate: presetStartDate,
    endDate: presetEndDate
  } = preset.getValue(Timezones.PORTAL_TZ);
  return areSimpleDatesEqual(startDate, presetStartDate) && areSimpleDatesEqual(endDate, presetEndDate);
});
export const subtractFromSimpleDate = (simpleDate, timeValue, momentUnits) => {
  const dateRangeSubtractedInMoment = toMomentPortalTz(simpleDate).subtract(timeValue, momentUnits);
  return fromMoment(dateRangeSubtractedInMoment);
};
export const subtractFromDateRange = (dateRange, timeValue, momentUnits) => {
  const {
    startDate,
    endDate
  } = dateRange;
  const newStartDate = subtractFromSimpleDate(startDate, timeValue, momentUnits);
  const newEndDate = subtractFromSimpleDate(endDate, timeValue, momentUnits);
  return {
    startDate: newStartDate,
    endDate: newEndDate
  };
};

// dev note - this is the recommended pattern to avoid spying on properties of namespace imports:
// https://product.hubteam.com/docs/frontend/kb/concatenated-spies.html
export const dateRangeUtilSettersForTesting = {
  setGetFirstDayOfWeekForTesting: func => {
    getFirstDayOfWeek = func;
  }
};